import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MpCodeTypes } from 'src/models/matchingProfiles/types';
import { useLoadMpCodes, useNutsFilter } from 'src/models/matchingProfiles/hooks';
import { Skeleton } from 'src/common';
import styles from './index.module.scss';
import PreferencesTree from 'src/shared/MatchingProfile/PreferencesTree';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import classNames from 'classnames';
import { toPreferencesTree } from 'src/models/matchingProfiles/helpers';
import GenericSearchDropdown, { IGenericSearchDropdownProps } from '../../GenericSearchDropDown';
import { trackTendersFilterCodes } from 'src/segment/events';

type Props = IGenericSearchDropdownProps & {
  type: MpCodeTypes;
  className?: string;
};
export const FilterCodes: FC<Props> = ({ type, className, ...restProps }) => {
  const { t } = useTranslation();
  const { data: codes } = useLoadMpCodes(type);
  const [{ vars, eventSource }, updateVars] = useApiTenders();
  const { data: nutsFilter, loading: nutsFilterLoading } = useNutsFilter(type !== 'nutsCodes');
  const isReady = codes && (type !== 'nutsCodes' || !nutsFilterLoading);

  const checkedKeys = useMemo(() => vars?.[type] ?? [], [type, vars]);
  const checkedKeysExact = useMemo(() => vars?.[`${type}Exact`], [type, vars]);
  const availableNodes = useMemo(
    () => (codes ? toPreferencesTree(type, codes, t, nutsFilter?.getNutsFilter) : []),
    [codes, nutsFilter?.getNutsFilter, t, type]
  );

  const updateCheckedKeys = useCallback(
    (_codes: string[]) => {
      updateVars({
        [type]: _codes
      });

      const optionId = _codes.find(item => !vars[type]?.includes(item));
      trackTendersFilterCodes(eventSource, type, optionId ?? _codes[0]);
    },
    [eventSource, type, updateVars, vars]
  );

  const isChanged = useMemo(
    () => !!checkedKeys.length || (!!checkedKeysExact && !!checkedKeysExact.length),
    [checkedKeys.length, checkedKeysExact]
  );

  return (
    <GenericSearchDropdown
      {...restProps}
      label={t(`Tenders.${type}`)}
      isChanged={isChanged}
      overlay={
        isReady ? (
          <PreferencesTree
            className={classNames(className, styles.tree)}
            availableNodes={availableNodes}
            checkedKeys={checkedKeys}
            checkedKeysExact={checkedKeysExact}
            updateCheckedKeys={updateCheckedKeys}
            checkedKeysUpdating={false}
            updatingErrorTitle={t('Tenders.hasNotUpdated', { itemName: t(`Tenders.${type}`) })}
            disabled={false}
            type={type}
            showKey
            checkStrictly
          />
        ) : (
          <Skeleton loading active />
        )
      }
      overlayClassName={styles.overlay}
    />
  );
};

export default FilterCodes;
